import React, { useEffect, useState, useRef } from "react";
import { DataGrid, GridFilterInputDate } from "@mui/x-data-grid";
import { Typography, Box, CircularProgress, Alert, Tooltip, Button, TextField, Autocomplete, Checkbox, IconButton } from "@mui/material";
import { API_ENDPOINTS } from "../config/api.config";

import { useCurrentUser } from '../hooks/useCurrentUser';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';


function getColumnsFromData(data) {
  if (!Array.isArray(data) || data.length === 0) return [];
  // Colonne principali flat secondo il nuovo tracciato
  return [
    { field: 'people_manager_name', headerName: 'People Manager', width: 180 },
    { field: 'user_name', headerName: 'User', width: 180 },
    { field: 'project_name', headerName: 'Project', width: 180 },
    { field: 'team_name', headerName: 'Team', width: 160 },
    { field: 'ticket_id', headerName: 'Ticket ID', width: 140 },
    { 
      field: 'date', 
      headerName: 'Date', 
      width: 130,
      type: 'date',
      filterable: true,
      valueFormatter: (params) => {
        if (!params.value) return '';
        const date = new Date(params.value);
        return date.toLocaleDateString('en-GB'); // Format as DD/MM/YYYY
      },
      filterOperators: [
        {
          label: 'Equals',
          value: 'equals',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
              return null;
            }
            return ({ value }) => {
              if (!value) {
                return false;
              }
              return value === filterItem.value;
            };
          },
          InputComponent: GridFilterInputDate,
        },
        {
          label: 'After',
          value: 'after',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
              return null;
            }
            return ({ value }) => {
              if (!value) {
                return false;
              }
              return new Date(value) > new Date(filterItem.value);
            };
          },
          InputComponent: GridFilterInputDate,
        },
        {
          label: 'Before',
          value: 'before',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
              return null;
            }
            return ({ value }) => {
              if (!value) {
                return false;
              }
              return new Date(value) < new Date(filterItem.value);
            };
          },
          InputComponent: GridFilterInputDate,
        },
      ],
    },
    { 
      field: 'effort', 
      headerName: 'Effort (min)', 
      width: 100,
      valueFormatter: (params) => {
        if (!params.value) return '';
        const minutes = Number(params.value);
        return `${minutes}`;
      }
    },
    { 
      field: 'effort_hours', 
      headerName: 'Effort (hours)', 
      width: 120,
      valueGetter: (params) => {
        // Usa il valore del campo 'effort' per calcolare le ore
        return params.row.effort;
      },
      valueFormatter: (params) => {
        if (!params.value) return '';
        const minutes = Number(params.value);
        const hours = Math.round((minutes / 60) * 100) / 100;
        return `${hours}`;
      }
    },
    { field: 'activity_name', headerName: 'Activity', width: 180 },
    { field: 'effort_type_name', headerName: 'Effort Type', width: 140 },
    { field: 'description', headerName: 'Description', width: 250 },
  ];
}

// Funzione per normalizzare la data a solo giorno (ignora orario e fuso)
function normalizeDate(dateStr) {
  const d = new Date(dateStr);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}



export default function TrackingListPeopleManagerView() {
  const { userId } = useCurrentUser();
  
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 5,
  });
  const [managedUsers, setManagedUsers] = useState([]);
  
  // Refs per evitare chiamate duplicate
  const isFetchingUsers = useRef(false);
  const isFetchingWorklogs = useRef(false);
  const lastWorklogsFetchKey = useRef('');
  const hasAutoSetFilterUsers = useRef(false);

  // Stato per l'espansione lazy dei sub-PM
  const [expandedPMs, setExpandedPMs] = useState(new Set());
  const [loadingPMs, setLoadingPMs] = useState(new Set());
  const [loadingAll, setLoadingAll] = useState(false);
  const [allHierarchyLoaded, setAllHierarchyLoaded] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const isExpandingRef = useRef(false);

  // Filtri per utente gestito e intervallo date
  const [filterUsers, setFilterUsers] = useState([]);
  // Imposta di default il mese corrente
  function getCurrentMonthRange() {
    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    // yyyy-mm-dd
    const toStr = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { start: toStr(firstDayOfMonth), end: toStr(lastDayOfMonth) };
  }
  const currentMonth = getCurrentMonthRange();
  const [filterStartDate, setFilterStartDate] = useState(currentMonth.start);
  const [filterEndDate, setFilterEndDate] = useState(currentMonth.end);

  // Stato per i filtri "pending" (modificabili dall'utente)
  const [pendingFilterUsers, setPendingFilterUsers] = useState(filterUsers);
  const [pendingFilterStartDate, setPendingFilterStartDate] = useState(filterStartDate);
  const [pendingFilterEndDate, setPendingFilterEndDate] = useState(filterEndDate);

  // Aggiorna i filtri effettivi solo quando si preme il pulsante
  const applyFilters = () => {
    setFilterUsers(pendingFilterUsers);
    setFilterStartDate(pendingFilterStartDate);
    setFilterEndDate(pendingFilterEndDate);
  };

  // Carica i report diretti di un PM (is_people_manager incluso nella risposta backend)
  const fetchDirectReports = async (pmId, pmName) => {
    const res = await fetch(`${API_ENDPOINTS.USERS}?id_people_manager=${pmId}`);
    const data = await res.json();
    const arr = data.users || [];

    return arr.map(user => {
      const userName = user.username || (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.email);
      return {
        id: user.id,
        name: userName,
        username: userName,
        email: user.email,
        isPeopleManager: !!user.is_people_manager,
        peopleManagerName: pmName,
        peopleManagerId: pmId,
      };
    });
  };

  // Raccoglie ricorsivamente gli ID di tutti i sub-PM espansi sotto un dato PM
  const collectDescendantPMIds = (pmId, users, expanded, visited = new Set()) => {
    if (visited.has(pmId)) return new Set();
    visited.add(pmId);
    const ids = new Set();
    const children = users.filter(u => u.peopleManagerId === pmId && u.isPeopleManager && expanded.has(u.id));
    for (const child of children) {
      ids.add(child.id);
      const subIds = collectDescendantPMIds(child.id, users, expanded, visited);
      for (const subId of subIds) ids.add(subId);
    }
    return ids;
  };

  // Espande o collassa i report di un sub-PM nel dropdown
  const toggleSubPmExpansion = async (pmUser) => {
    const pmId = pmUser.id;
    isExpandingRef.current = true;

    try {
      if (expandedPMs.has(pmId)) {
        const descendantPMIds = collectDescendantPMIds(pmId, managedUsers, expandedPMs);
        const allPMIdsToCollapse = new Set([pmId, ...descendantPMIds]);

        const removedNames = new Set(
          managedUsers
            .filter(u => allPMIdsToCollapse.has(u.peopleManagerId))
            .map(u => u.name || u.username)
        );
        setPendingFilterUsers(prev => prev.filter(n => !removedNames.has(n)));

        setManagedUsers(prev => prev.filter(u => !allPMIdsToCollapse.has(u.peopleManagerId)));
        setExpandedPMs(prev => {
          const next = new Set(prev);
          for (const id of allPMIdsToCollapse) next.delete(id);
          return next;
        });
      } else {
        setLoadingPMs(prev => new Set(prev).add(pmId));
        try {
          const reports = await fetchDirectReports(pmId, pmUser.name || pmUser.username);
          setManagedUsers(prev => {
            const pmIndex = prev.findIndex(u => u.id === pmId);
            const before = prev.slice(0, pmIndex + 1);
            const after = prev.slice(pmIndex + 1);
            return [...before, ...reports, ...after];
          });
          setExpandedPMs(prev => new Set(prev).add(pmId));
          const newNames = reports.map(u => u.name || u.username);
          setPendingFilterUsers(prev => [...new Set([...prev, ...newNames])]);
        } catch (err) {
          console.error(`Error expanding PM ${pmId}:`, err);
        } finally {
          setLoadingPMs(prev => {
            const next = new Set(prev);
            next.delete(pmId);
            return next;
          });
        }
      }
    } finally {
      setTimeout(() => { isExpandingRef.current = false; }, 150);
    }
  };

  // Carica ricorsivamente tutta la gerarchia (on-demand, quando si clicca "All")
  const loadAllHierarchy = async () => {
    if (!userId || loadingAll) return;
    setLoadingAll(true);
    try {
      const allUsers = [];
      const newExpandedPMs = new Set();
      const visitedIds = new Set();

      const expandRecursively = async (pmId, pmName) => {
        if (visitedIds.has(pmId)) return;
        visitedIds.add(pmId);

        const reports = await fetchDirectReports(pmId, pmName);
        allUsers.push(...reports);

        const subPMs = reports.filter(u => u.isPeopleManager);
        subPMs.forEach(pm => newExpandedPMs.add(pm.id));

        await Promise.all(subPMs.map(pm =>
          expandRecursively(pm.id, pm.name || pm.username)
        ));
      };

      await expandRecursively(userId, 'Direct reports');

      setManagedUsers(allUsers);
      setExpandedPMs(newExpandedPMs);
      setPendingFilterUsers(allUsers.map(u => u.name || u.username));
      setAllHierarchyLoaded(true);
    } catch (err) {
      console.error('Error loading full hierarchy:', err);
    } finally {
      setLoadingAll(false);
    }
  };

  // Carica solo i direct reports all'avvio
  useEffect(() => {
    if (!userId) {
      setManagedUsers([]);
      return;
    }

    if (isFetchingUsers.current) {
      return;
    }

    hasAutoSetFilterUsers.current = false;
    lastWorklogsFetchKey.current = '';
    setExpandedPMs(new Set());
    setAllHierarchyLoaded(false);

    isFetchingUsers.current = true;
    console.log('Fetching direct reports for people manager userId:', userId);

    fetchDirectReports(userId, 'Direct reports')
      .then(directReports => {
        console.log('Direct reports loaded:', directReports.length);
        setManagedUsers(directReports);
        isFetchingUsers.current = false;
      })
      .catch((err) => {
        console.error('Error fetching direct reports:', err);
        setManagedUsers([]);
        isFetchingUsers.current = false;
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Imposta automaticamente "Select All" per tutti gli utenti gestiti
  // Usa useRef per evitare loop infiniti quando filterUsers cambia
  useEffect(() => {
    if (managedUsers.length > 0 && !hasAutoSetFilterUsers.current) {
      // Imposta tutti gli utenti gestiti come selezionati (Select All)
      const allUserNames = managedUsers.map(u => u.name || u.username);
      setPendingFilterUsers(allUserNames);
      hasAutoSetFilterUsers.current = true;
      console.log('Auto-setting Select All for managed users:', allUserNames);
    }
  }, [managedUsers]);

  // Sposto fetchWorklogs fuori da useEffect
  const fetchWorklogs = async () => {
    // Crea una chiave univoca per questa combinazione di parametri
    const userIdsToFilter = filterUsers.length > 0
      ? managedUsers
          .filter(u => filterUsers.includes(u.name || u.username))
          .map(u => u.id)
          .sort()
          .join(',')
      : managedUsers.map(u => u.id).sort().join(',');
    
    const fetchKey = `${filterStartDate || ''}-${filterEndDate || ''}-${userIdsToFilter}`;
    
    // Evita chiamate duplicate per la stessa combinazione di parametri
    if (isFetchingWorklogs.current) {
      return;
    }
    
    // Se la chiave è la stessa, evita la chiamata (dati già caricati)
    if (lastWorklogsFetchKey.current === fetchKey) {
      return;
    }
    
    // Reset del flag se la chiave è diversa (nuovi parametri)
    if (lastWorklogsFetchKey.current && lastWorklogsFetchKey.current !== fetchKey) {
      isFetchingWorklogs.current = false;
    }

    isFetchingWorklogs.current = true;
    lastWorklogsFetchKey.current = fetchKey;
    setLoading(true);
    setError(null);
    
    try {
      // Costruisci la query string con i filtri data
      const params = [];
      if (filterStartDate) params.push(`date_from=${encodeURIComponent(filterStartDate)}`);
      if (filterEndDate) params.push(`date_to=${encodeURIComponent(filterEndDate)}`);
      
      // Filtra per utenti gestiti
      let userIdsArray = [];
      
      if (filterUsers.length > 0) {
        // Filtra solo gli utenti selezionati dall'utente
        userIdsArray = managedUsers
          .filter(u => filterUsers.includes(u.name || u.username))
          .map(u => u.id);
        console.log('Filtering worklogs for selected user names:', filterUsers);
        console.log('Selected user IDs:', userIdsArray);
      } else {
        // Nessun filtro specifico: usa tutti gli utenti gestiti (comportamento "All")
        userIdsArray = managedUsers.map(u => u.id);
        console.log('No user filter - using all managed user IDs:', userIdsArray);
      }
      
      if (userIdsArray.length === 0) {
        // Nessun utente selezionato o gestito, restituisci array vuoto
        setRows([]);
        setColumns([]);
        setLoading(false);
        isFetchingWorklogs.current = false;
        return;
      }
      
      const BATCH_SIZE = 50;
      const batches = [];
      for (let i = 0; i < userIdsArray.length; i += BATCH_SIZE) {
        batches.push(userIdsArray.slice(i, i + BATCH_SIZE));
      }
      
      console.log(`Worklogs: ${userIdsArray.length} users in ${batches.length} batch(es)`);
      
      const batchResults = await Promise.all(batches.map(async (batchIds) => {
        const batchParams = [...params, `user_ids=${encodeURIComponent(batchIds.join(','))}`];
        const url = API_ENDPOINTS.WORKLOGS + '?' + batchParams.join('&');
        const response = await fetch(url);
        if (!response.ok) {
          const errorText = await response.text();
          console.error(`Batch error (${batchIds.length} users):`, errorText);
          return [];
        }
        const data = await response.json();
        return data.worklogs || [];
      }));
      
      const allWorklogs = batchResults.flat();
      console.log('Dati ricevuti (totali):', allWorklogs.length);
      
      setRows(allWorklogs.map((row, idx) => ({ id: row.id || idx, ...row })));
      setColumns(getColumnsFromData(allWorklogs));
      isFetchingWorklogs.current = false;
    } catch (err) {
      setError(err.message);
      isFetchingWorklogs.current = false;
    } finally {
      setLoading(false);
    }
  };

  // Aggiorno la useEffect che richiama fetchWorklogs per dipendere solo dai filtri effettivi
  useEffect(() => {
    if (!userId || managedUsers.length === 0) return;
    fetchWorklogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, filterUsers, filterStartDate, filterEndDate, managedUsers]);

  // Sostituisco il filtro rows con log dettagliato
  const filteredRows = rows.filter(row => {
    let match = true;
    if (filterUsers.length > 0) {
      if (!filterUsers.includes(row.user_name)) {
        console.log('Riga esclusa per user:', row);
        match = false;
      }
    }
    if (filterStartDate) {
      if (!(row.date && normalizeDate(row.date) >= normalizeDate(filterStartDate))) {
        console.log('Riga esclusa per start date:', row);
        match = false;
      }
    }
    if (filterEndDate) {
      if (!(row.date && normalizeDate(row.date) <= normalizeDate(filterEndDate))) {
        console.log('Riga esclusa per end date:', row);
        match = false;
      }
    }
    return match;
  });
  console.log('Filtri attivi:', filterUsers, filterStartDate, filterEndDate);
  console.log('Righe mostrate:', filteredRows);




  // Tooltip per le colonne Description e Project Name
  const columnsWithDescriptionTooltip = columns.map(col => {
    if (col.field === 'description' || col.field === 'project_name') {
      return {
        ...col,
        renderCell: (params) => (
          <Tooltip title={params.value || ''} placement="top" arrow>
            <span style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block',
              width: '100%'
            }}>{params.value}</span>
          </Tooltip>
        )
      };
    }
    return col;
  });

  // Funzione per esportare la tabella in Excel
  const handleExportExcel = () => {
    if (!filteredRows || filteredRows.length === 0) return;
    const dataToExport = filteredRows.map(row => {
      const obj = {};
      columns.forEach(col => {
        if (col.field === 'effort') {
          // Per la colonna effort, mantieni il valore numerico per i calcoli Excel
          obj[col.headerName || col.field] = row[col.field] || 0;
          return;
        }
        if (col.field === 'effort_hours') {
          // Calcola le ore a partire dai minuti (due decimali) - mantieni valore numerico per Excel
          const minutes = Number(row.effort || 0);
          const hours = minutes ? Math.round((minutes / 60) * 100) / 100 : 0;
          obj[col.headerName || col.field] = hours;
          return;
        }
        if (col.field === 'date') {
          // Formatta la data per Excel (YYYY-MM-DD) evitando problemi di timezone
          const dateValue = row[col.field];
          if (dateValue) {
            let formattedDate, year, month;
            
            // Usa la data direttamente se è già in formato YYYY-MM-DD
            if (typeof dateValue === 'string' && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
              formattedDate = dateValue;
              [year, month] = dateValue.split('-');
            } else {
              // Altrimenti converte evitando problemi di timezone
              const date = new Date(dateValue);
              year = date.getFullYear();
              month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              formattedDate = `${year}-${month}-${day}`;
            }
            
            obj[col.headerName || col.field] = formattedDate;
            // Aggiungi colonne Month e Year
            obj['Month'] = month;
            obj['Year'] = year;
          } else {
            obj[col.headerName || col.field] = '';
            obj['Month'] = '';
            obj['Year'] = '';
          }
          return;
        }
        obj[col.headerName || col.field] = row[col.field];
      });
      return obj;
    });
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Worklogs');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    
    // Genera il nome del file con l'intervallo di date
    let fileName = 'worklogs';
    
    // Funzione per convertire YYYY-MM-DD in DDMMYYYY
    const formatDateForFileName = (dateStr) => {
      if (!dateStr) return '';
      const [year, month, day] = dateStr.split('-');
      return `${day}${month}${year}`;
    };
    
    // Funzione per pulire il nome dell'utente per il nome del file
    const cleanUserName = (userName) => {
      if (!userName) return '';
      return userName.replace(/[^a-zA-Z0-9]/g, '_'); // Sostituisce caratteri speciali con underscore
    };
    
    const userName = filterUsers.length === 1 ? cleanUserName(filterUsers[0]) : 
                     filterUsers.length > 1 ? 'Multiple_Users' : '';
    
    if (filterStartDate && filterEndDate) {
      const startFormatted = formatDateForFileName(filterStartDate);
      const endFormatted = formatDateForFileName(filterEndDate);
      if (userName) {
        fileName = `worklogs_${userName}_${startFormatted}-${endFormatted}`;
      } else {
        fileName = `Worklogs_${startFormatted}-${endFormatted}`;
      }
    } else if (filterStartDate) {
      const startFormatted = formatDateForFileName(filterStartDate);
      if (userName) {
        fileName = `worklogs_${userName}_from_${startFormatted}`;
      } else {
        fileName = `Worklogs_from_${startFormatted}`;
      }
    } else if (filterEndDate) {
      const endFormatted = formatDateForFileName(filterEndDate);
      if (userName) {
        fileName = `worklogs_${userName}_until_${endFormatted}`;
      } else {
        fileName = `Worklogs_until_${endFormatted}`;
      }
    } else if (userName) {
      fileName = `worklogs_${userName}`;
    }
    
    saveAs(blob, `${fileName}.xlsx`);
  };

  return (
    <Box>
      <Box my={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" gutterBottom sx={{ fontSize: '1.5rem' }}>People Manager Tracking List</Typography>
          <Button variant="outlined" color="primary" onClick={handleExportExcel} startIcon={<DownloadIcon />}>Excel</Button>
        </Box>
        {/* Filtri utente gestito e intervallo date */}
        <Box 
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          gap={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent={{ xs: 'flex-start', md: 'flex-start' }}
          mb={2}
        >
          <Autocomplete
            multiple
            disableCloseOnSelect={true}
            open={dropdownOpen}
            onOpen={() => setDropdownOpen(true)}
            onClose={() => {
              if (!isExpandingRef.current) {
                setDropdownOpen(false);
              }
            }}
            options={[
              { id: 'all', name: 'Display all', isSpecial: true },
              ...managedUsers
            ]}
            groupBy={(option) => option.isSpecial ? '' : (option.peopleManagerName || 'Direct reports')}
            getOptionLabel={(option) => option.name || option.username || option.email}
            value={managedUsers.filter(u => pendingFilterUsers.includes(u.name || u.username))}
            onChange={(event, newValue) => {
              const clickedAll = newValue.some(v => v.id === 'all');
              
              if (clickedAll) {
                if (allHierarchyLoaded) {
                  const allNames = managedUsers.map(u => u.name || u.username);
                  const allSelected = allNames.every(n => pendingFilterUsers.includes(n));
                  if (allSelected) {
                    setPendingFilterUsers([]);
                    setAllHierarchyLoaded(false);
                  } else {
                    setPendingFilterUsers(allNames);
                  }
                } else {
                  loadAllHierarchy();
                }
              } else {
                setPendingFilterUsers(newValue.map(u => u.name || u.username));
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Managed Users"
                placeholder="Search users..."
                size="small"
                sx={{ minWidth: 300 }}
              />
            )}
            renderGroup={(params) => {
              if (!params.group) {
                return <React.Fragment key={params.key}>{params.children}</React.Fragment>;
              }
              const groupUsers = managedUsers.filter(u => (u.peopleManagerName || 'Direct reports') === params.group);
              const groupNames = groupUsers.map(u => u.name || u.username);
              const allGroupSelected = groupNames.length > 0 && groupNames.every(n => pendingFilterUsers.includes(n));
              const someGroupSelected = groupNames.some(n => pendingFilterUsers.includes(n));
              return (
                <li key={params.key}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      bgcolor: 'grey.100',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      px: 1,
                      py: 0.25,
                      cursor: 'pointer',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (allGroupSelected) {
                        setPendingFilterUsers(prev => prev.filter(n => !groupNames.includes(n)));
                      } else {
                        setPendingFilterUsers(prev => [...new Set([...prev, ...groupNames])]);
                      }
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={allGroupSelected}
                      indeterminate={!allGroupSelected && someGroupSelected}
                      sx={{ p: 0.5, mr: 0.5, pointerEvents: 'none' }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 'bold', color: 'text.secondary' }}
                    >
                      {params.group === 'Direct reports' ? params.group : `Reports of ${params.group}`}
                    </Typography>
                  </Box>
                  <ul style={{ padding: 0 }}>{params.children}</ul>
                </li>
              );
            }}
            renderOption={(props, option, { selected }) => {
              if (option.isSpecial) {
                return (
                  <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                      checked={allHierarchyLoaded && managedUsers.length > 0 && managedUsers.every(u => pendingFilterUsers.includes(u.name || u.username))}
                      sx={{ mr: 1, pointerEvents: 'none' }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontStyle: 'italic', color: 'black' }}
                    >
                      {option.name}
                    </Typography>
                    {loadingAll && (
                      <CircularProgress size={16} sx={{ ml: 1 }} />
                    )}
                  </Box>
                );
              }
              
              return (
                <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                    <Checkbox
                      checked={selected}
                      sx={{ mr: 1 }}
                    />
                    <Box>
                      <Typography variant="body2">
                        {option.name || option.username}
                        {option.isPeopleManager && (
                          <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'primary.main' }}>
                            (PM)
                          </Typography>
                        )}
                      </Typography>
                      {option.email && (
                        <Typography variant="caption" color="textSecondary">
                          {option.email}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  {option.isPeopleManager && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        toggleSubPmExpansion(option);
                      }}
                      sx={{ ml: 1, flexShrink: 0 }}
                    >
                      {loadingPMs.has(option.id) ? (
                        <CircularProgress size={18} />
                      ) : expandedPMs.has(option.id) ? (
                        <ExpandLessIcon fontSize="small" />
                      ) : (
                        <ExpandMoreIcon fontSize="small" />
                      )}
                    </IconButton>
                  )}
                </Box>
              );
            }}
            renderTags={(value, getTagProps) => {
              if (allHierarchyLoaded && managedUsers.length > 0 && managedUsers.every(u => pendingFilterUsers.includes(u.name || u.username))) {
                return <Typography variant="body2" color="textSecondary">All users ({managedUsers.length})</Typography>;
              }
              if (value.length === 0) {
                return <Typography variant="body2" color="textSecondary">Select users...</Typography>;
              }
              return <Typography variant="body2">{value.length} selected</Typography>;
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText="No users found"
            clearOnBlur={false}
            selectOnFocus
            handleHomeEndKeys
            filterOptions={(options, { inputValue }) => {
              const specialOptions = options.filter(opt => opt.isSpecial);
              const regularOptions = options.filter(opt => !opt.isSpecial);
              
              const filteredRegular = regularOptions.filter(option =>
                (option.name || option.username || '').toLowerCase().includes(inputValue.toLowerCase()) ||
                (option.email && option.email.toLowerCase().includes(inputValue.toLowerCase()))
              );
              
              return [...specialOptions, ...filteredRegular];
            }}
          />
          <TextField
            label="Start Date"
            type="date"
            size="small"
            value={pendingFilterStartDate}
            onChange={e => setPendingFilterStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Date"
            type="date"
            size="small"
            value={pendingFilterEndDate}
            onChange={e => setPendingFilterEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="contained" size="small" color="primary" onClick={applyFilters} startIcon={<FilterListIcon />}>Search</Button>
        </Box>
        {loading ? (
          <CircularProgress />
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={filteredRows}
              columns={columnsWithDescriptionTooltip}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[5, 10, 25, 50, 100]}
              pagination
              hideFooter={false}
              sx={{ bgcolor: 'white' }}
              filterMode="client"
              disableColumnFilter={false}
              disableColumnMenu={false}
            />
          </Box>
        )}
      </Box>

    </Box>
  );
} 

